import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableBody, useTheme } from "@mui/material";
import { Grid, Typography, TableRow, TableCell, Divider } from "@mui/material";
import React from "react";
import { useState, useEffect, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import EmployeeDependent from "./EmployeeDependent";
import { State } from "./interfaces";
import { Container, StyledTable, StyledTableCell } from "./styles";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeDependentDTO } from "../../../models/employee-dependents/employee-dependent-dto";
import { EmployeeDependentSTO } from "../../../models/employee-dependents/employee-dependent-sto";
import AddEditItem from "../../../reusable-components/Table/AddEditItem/AddEditItem";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";

import { EmployeeDependentTypesApiService } from "../../../services/employee-dependent-types/employee-dependent-types-api.service";
import { loadEmployeeDependentMutations } from "../../../services/employees/employees-current-dependents-mutations-api.service";
import { loadEmployeeDependents } from "../../../services/employees/employees-dependents-api.service";
import { EmployeesDependentsApiService } from "../../../services/employees/employees-dependents-api.service";

import { EmployeeDataContext } from "../EmployeePersonalData";

const EmployeeDependents = () => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path
  const theme = useTheme();
  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    dependents: [],
    mutations: [],
  });

  const getEmployeeDependents = useCallback(async (employeeId: string, signal?: AbortSignal) => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const { items } = await loadEmployeeDependents(employeeId, forManager, signal);

      const mutationsResponse = await loadEmployeeDependentMutations(employeeId, items, signal);

      if (!signal || !signal.aborted)
        setState((state) => ({
          ...state,
          dependents: items,
          mutations: mutationsResponse.items,
        }));
    } catch (error) {
      if (!signal || !signal.aborted) setState((state) => ({ ...state, error }));
    } finally {
      if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    if (employee?.id) getEmployeeDependents(employee.id, abortController.signal);

    return () => abortController.abort();
  }, [employee?.id, getEmployeeDependents]);

  const newEmployeeDependent: EmployeeDependentSTO = {
    id: uuidv4(),
    isDeleted: false,
    employee,
    companyAccount: employee?.companyAccount ?? null,
    dependentType: null,
    name: "",
    firstName: "",
    lastName: "",
    birthDate: new Date(),
    toDate: new Date(9999, 11, 31),
  };

  const formFields: FormField[] = [
    {
      labelKey: t(`${tRP}.employee_data.employee_dependents.fields.dependentType`),
      field: "dependentType",
      fieldType: "dropdown",
      apiService: new EmployeeDependentTypesApiService(),
    },
    {
      labelKey: t(`${tRP}.employee_data.employee_dependents.fields.firstName`),
      field: "firstName",
    },
    {
      labelKey: t(`${tRP}.employee_data.employee_dependents.fields.lastName`),
      field: "lastName",
    },
    {
      labelKey: t(`${tRP}.employee_data.employee_dependents.fields.birthDate`),
      field: "birthDate",
      fieldType: "datePicker",
    },
    {
      labelKey: t(`${tRP}.employee_data.employee_dependents.fields.toDate`),
      field: "toDate",
      fieldType: "datePicker",
    },
  ];

  const findMutation = ({ dependentType, id }: EmployeeDependentDTO) =>
    state.mutations.find((m) => m.dependentType?.id === dependentType?.id && m.original?.id === id);

  return (
    <Container>
      <LoadingOverlay loading={state.loading}>
        <StyledTable>
          <TableBody>
            <TableRow>
              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0, width: "160px" }}
              >
                <Grid
                  style={{
                    borderRadius: "5px",
                    backgroundColor: theme.palette.background.paper,
                    border: "none",
                    padding: "0px 5px",
                    height: "45px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6">{t(`${tRP}.employee_data.employee_dependents.title`)}</Typography>
                  {state.dependents.length > 0 && (
                    <AddEditItem
                      titleForEditComponent={t(`${tRP}.employee_data.employee_dependents.add_title`)}
                      item={newEmployeeDependent}
                      formFields={formFields}
                      serviceApiInstance={new EmployeesDependentsApiService()}
                      handleReload={() => getEmployeeDependents(employee?.id ?? "")}
                      customAddEditButtonStyle={{
                        width: "unset",
                        height: "unset",
                        // padding: "8px",
                        color: theme.palette.primary.main,
                        backgroundColor: "transparent",
                        borderRadius: theme.shape.borderRadius,
                      }}
                    >
                      <Typography>
                        <FontAwesomeIcon icon={faPlus} /> &nbsp;
                        {`${t(`${tRP}.employee_data.employee_dependents.add_title`)}`}
                      </Typography>
                    </AddEditItem>
                  )}
                </Grid>
              </TableCell>
              <TableCell
                rowSpan={6}
                style={{ verticalAlign: "top", padding: 0 }}
              >
                <Divider
                  flexItem
                  orientation="horizontal"
                  style={{
                    width: "100%",
                    minWidth: "10px",
                    marginTop: "20px",
                    border: `3px solid ${theme.palette.background.paper}`,
                  }}
                />
              </TableCell>

              {state.dependents.length > 0 ? (
                <>
                  <EmployeeDependent
                    dependent={state.dependents[0]}
                    mutation={findMutation(state.dependents[0])}
                    handleReload={() => getEmployeeDependents(employee?.id ?? "")}
                  />
                </>
              ) : (
                <StyledTableCell>
                  <AddEditItem
                    titleForEditComponent={t(`${tRP}.employee_data.employee_dependents.add_title`)}
                    item={newEmployeeDependent}
                    formFields={formFields}
                    serviceApiInstance={new EmployeesDependentsApiService()}
                    handleReload={() => getEmployeeDependents(employee?.id ?? "")}
                    customAddEditButtonStyle={{
                      width: "unset",
                      height: "unset",
                      padding: "8px",
                      color: theme.palette.primary.main,
                      backgroundColor: "transparent",
                      borderRadius: theme.shape.borderRadius,
                    }}
                  >
                    <Typography>
                      <FontAwesomeIcon icon={faPlus} /> &nbsp;
                      {`${t(`${tRP}.employee_data.employee_dependents.add_title`)}`}
                    </Typography>
                  </AddEditItem>
                </StyledTableCell>
              )}
            </TableRow>

            {state.dependents.slice(1).map((dependent, index) => (
              <TableRow key={index}>
                <EmployeeDependent
                  key={index}
                  dependent={dependent}
                  mutation={findMutation(dependent)}
                  handleReload={() => getEmployeeDependents(employee?.id ?? "")}
                />
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </LoadingOverlay>
    </Container>
  );
};

export default EmployeeDependents;
